import { Injectable } from '@angular/core';
import { environment } from '@cpd-app/environment';
import { Angulartics2GoogleTagManager } from 'angulartics2';

// eslint-disable-next-line @typescript-eslint/ban-types
declare let gtag: Function;

/*
 * THE CPD EVENT VOCABULARY (DS-T16)
 *
 * Until this, GA fired `page_view` and nothing else, so we could say which
 * pages were opened and nothing whatever about whether the product was used.
 * "How many learners uploaded a transcript" was unanswerable from analytics.
 *
 * These five are deliberately the whole list. Each one is a step a learner
 * either took or did not take, and together they are the funnel the DANTES
 * reporting story needs. Resist adding a sixth without deciding what question
 * it answers -- an event nobody queries is a maintenance cost that looks like
 * diligence.
 *
 *   jst_upload_started    A transcript file was handed to the parser. Fired at
 *                         the request, not at the file picker, so it pairs with
 *                         the completion below.
 *   jst_upload_completed  The learner CONFIRMED the parsed transcript and it
 *                         saved. Not the parse finishing: a parse the learner
 *                         abandons at the review step is a started upload that
 *                         never completed, and the gap between the two is the
 *                         interesting number.
 *   goal_set              A career or academic goal was written. `source` is
 *                         'ui' when the learner set it themselves and 'via'
 *                         when Via extracted it from a conversation. Those are
 *                         genuinely different products behaving differently, so
 *                         the dimension is not optional.
 *   report_generated      The learner produced their goals report PDF, i.e. the
 *                         download succeeded. Not the report page loading,
 *                         which is a page_view and already covered.
 *   via_opened            The Via chat surface was opened.
 *
 * PARAMETER NAMES: GA4 reserves the `google_`, `ga_` and `firebase_` prefixes,
 * and drops parameters it does not recognize unless they are registered as
 * custom dimensions in the property. `source` must be registered in GA4 before
 * it appears in reports. The event itself arrives either way, so an
 * unregistered dimension looks like working analytics with one blank column.
 *
 * NO NEW TABLES. Three home-grown trackers already exist
 * (`analytics.userreportstracker`, `userreportssharedtracker`,
 * `lastuseractivity`) and nothing reads any of them. Where one already fires at
 * the same moment as an event here, the GA call sits ALONGSIDE it rather than
 * replacing it, so this change removes nothing that might turn out to be load
 * bearing.
 */
export const CPD_ANALYTICS_EVENT = {
  jstUploadStarted: 'jst_upload_started',
  jstUploadCompleted: 'jst_upload_completed',
  goalSet: 'goal_set',
  reportGenerated: 'report_generated',
  viaOpened: 'via_opened'
} as const;

export type CpdAnalyticsEvent =
  (typeof CPD_ANALYTICS_EVENT)[keyof typeof CPD_ANALYTICS_EVENT];

/** Where a goal came from. See `goal_set` above. */
export type GoalSetSource = 'ui' | 'via';

@Injectable({
  providedIn: 'root'
})
export class GoogleAnalyticsService {
  constructor(private angularticsGTM: Angulartics2GoogleTagManager) {}

  public initialize(): void {
    try {
      const trackingId = environment.googleAnalyticsTrackingId;

      /*
       * DS-T16: refuse a dead property rather than pretending to measure.
       *
       * dev, staging and local were all configured with Universal Analytics
       * property ids (`UA-167347566-2`, `UA-150258729-1`, `UA-117869959-1`).
       * Universal Analytics stopped processing data in July 2023, so those
       * environments have measured precisely nothing for over three years while
       * looking fully instrumented: the script loaded, the events fired, and
       * the data went nowhere.
       *
       * Failing loudly is the point. A silent no-op would reproduce the exact
       * problem this fixes -- analytics that appear to work. Configure a GA4
       * measurement id (`G-...`), or a GA4 debug property, and this starts
       * working with no other change.
       */
      if (!trackingId) {
        console.warn(
          '[analytics] no googleAnalyticsTrackingId for this environment; Google Analytics is off and no events will be sent'
        );
        return;
      }
      if (trackingId.startsWith('UA-')) {
        console.warn(
          `[analytics] googleAnalyticsTrackingId "${trackingId}" is a Universal Analytics property. UA stopped processing data in July 2023, so nothing sent here would be recorded. Google Analytics stays off until a GA4 measurement id (G-...) is configured for this environment.`
        );
        return;
      }

      const url = 'https://www.googletagmanager.com/gtag/js?id=';
      const gTagScript = document.createElement('script');
      gTagScript.type = 'text/plain';
      gTagScript.setAttribute('data-cookie-consent', 'tracking');
      gTagScript.async = true;
      gTagScript.src = `${url}${trackingId}`;
      document.head.appendChild(gTagScript);

      const dataLayerScript = document.createElement('script');
      dataLayerScript.innerHTML = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${trackingId}');`;
      document.head.appendChild(dataLayerScript);
      this.angularticsGTM.startTracking();
    } catch (e) {
      console.error('Error adding Google Analytics', e);
    }
  }

  public emitEvent(url: string): void {
    this.send('page_view', { page_path: url });
  }

  /**
   * Records one of the five CPD product events.
   *
   * Typed against CPD_ANALYTICS_EVENT rather than taking a string, because the
   * failure mode of free-text event names is a report that silently splits one
   * behavior across `via_opened`, `viaOpened` and `via_open` and undercounts
   * all three.
   */
  public trackEvent(
    event: CpdAnalyticsEvent,
    params: Record<string, string | number | boolean> = {}
  ): void {
    this.send(event, params);
  }

  /*
   * The one place that calls gtag.
   *
   * Guarded and non-throwing, deliberately. `gtag` is undefined whenever
   * initialize() bailed out -- LOCAL, a missing id, a dead UA property -- and
   * these calls sit inside NgRx effects and subscribe handlers on the goal-save
   * and transcript-upload paths. An exception there would not merely lose a
   * metric, it would tear down the subscription or fail the effect, so a
   * measurement bug could stop a learner saving a goal. Analytics must never be
   * able to do that.
   *
   * Consent is handled a layer up and is not this method's business: the gtag
   * loader script carries `data-cookie-consent="tracking"`, so before consent
   * these calls queue in dataLayer and are never transmitted.
   */
  private send(
    eventName: string,
    params: Record<string, string | number | boolean>
  ): void {
    try {
      if (typeof gtag !== 'function') {
        return;
      }
      gtag('event', eventName, params);
    } catch (e) {
      console.error('[analytics] failed to send event', eventName, e);
    }
  }
}
